import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CopyButton } from "@/components/copy-button";
import { formatStatusLabel } from "@/lib/utils";
import { ApiError } from "@/api/client";
import { disableTwoFactor, enableTwoFactor, getTwoFactorStatus, setupTwoFactor } from "@/api/auth";
import { type TwoFactorSetupResponse } from "@/api/types";

// New page (Task 6, feature-029) — the first profile/account page in this
// app. Read-only admin identity comes from the `_app` route's already-loaded
// `/auth/me` context (no extra fetch); the 2FA section is the only
// interactive part, driven by GET /v1/auth/2fa/status plus the
// setup/enable/disable wizard. SectionCard/DetailRow mirror the pattern
// already used by `_app.merchants_.$merchantId.tsx` (Card/CardHeader/
// CardContent-based, not apps/merchant's raw-<div> version) since this app
// already has its own local convention for it.
export const Route = createFileRoute("/_app/profile")({
  component: ProfilePage,
});

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="space-y-1.5">
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";

// ─── identity section ───────────────────────────────────────────────────────

function IdentitySection() {
  const { admin } = Route.useRouteContext();

  return (
    <dl className="space-y-3">
      <DetailRow label="Display name" value={admin.displayName} />
      <DetailRow label="Email" value={admin.email} />
      <DetailRow
        label="Roles"
        value={
          <div className="flex flex-wrap justify-end gap-1">
            {admin.roles.map((role) => (
              <span
                key={role}
                className="inline-flex items-center rounded-full bg-[#EEF2FF] px-2 py-0.5 text-xs font-medium text-[#3730A3]"
              >
                {formatStatusLabel(role)}
              </span>
            ))}
          </div>
        }
      />
    </dl>
  );
}

// ─── enable-2FA wizard ──────────────────────────────────────────────────────
//
// Three states: (1) idle — just the "Enable 2FA" button; (2) `setup` —
// setupTwoFactor() already returned the QR/secret, admin enters a code to
// confirm via enableTwoFactor(); (3) `backupCodes` — the 10 one-time codes
// from enableTwoFactor()'s response, shown exactly once (per the spec) with
// a "Continue" button that just dismisses them — they're never re-fetchable.

type EnableWizardStep = "idle" | "setup" | "backupCodes";

function EnableTwoFactorWizard({ onEnabled }: { onEnabled: () => void }) {
  const [step, setStep] = useState<EnableWizardStep>("idle");
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const setupMutation = useMutation({
    mutationFn: setupTwoFactor,
    onSuccess: (data) => {
      setSetupData(data);
      setStep("setup");
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to start 2FA enrollment.");
    },
  });

  const enableMutation = useMutation({
    mutationFn: () => enableTwoFactor(code),
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setStep("backupCodes");
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Invalid code. Please try again.");
    },
  });

  const startEnrollment = () => {
    setError(null);
    setupMutation.mutate();
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!code) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    enableMutation.mutate();
  };

  const finish = () => {
    setStep("idle");
    setSetupData(null);
    setCode("");
    setBackupCodes([]);
    onEnabled();
  };

  if (step === "idle") {
    return (
      <div className="space-y-3">
        {error && <div className={ERROR_BOX}>{error}</div>}
        <Button onClick={startEnrollment} disabled={setupMutation.isPending}>
          {setupMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enable 2FA"}
        </Button>
      </div>
    );
  }

  if (step === "setup" && setupData) {
    return (
      <div className="space-y-4 rounded-lg border border-border p-4">
        {error && <div className={ERROR_BOX}>{error}</div>}
        <p className="text-sm text-muted-foreground">
          Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password,
          etc.), or enter the key manually.
        </p>
        <div className="flex justify-center">
          <img
            src={setupData.qrCodeDataUrl}
            alt="Two-factor authentication QR code"
            className="h-40 w-40 rounded-md border border-border"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Manual entry key</Label>
          <div className="flex items-center gap-1">
            <code className="flex-1 truncate rounded-md border border-input bg-muted px-2 py-1.5 text-xs">
              {setupData.secret}
            </code>
            <CopyButton value={setupData.secret} label="Copy manual entry key" />
          </div>
        </div>
        <form onSubmit={submitCode} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="enable-code">2FA code</Label>
            <Input
              id="enable-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              minLength={6}
              maxLength={9}
              autoComplete="one-time-code"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStep("idle");
                setSetupData(null);
                setCode("");
                setError(null);
              }}
              disabled={enableMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={enableMutation.isPending}>
              {enableMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify and enable"}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // step === "backupCodes"
  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[#10B981]" />
        <p className="text-sm font-medium">Two-factor authentication is now enabled.</p>
      </div>
      <p className="text-sm text-muted-foreground">
        Save these backup codes somewhere safe. Each one can be used once to sign in if you lose
        access to your authenticator app. They will not be shown again.
      </p>
      <div className="grid grid-cols-2 gap-2 rounded-md border border-border bg-muted p-3 font-mono text-sm sm:grid-cols-3">
        {backupCodes.map((backupCode) => (
          <span key={backupCode}>{backupCode}</span>
        ))}
      </div>
      <Button onClick={finish}>Continue</Button>
    </div>
  );
}

// ─── disable-2FA form ───────────────────────────────────────────────────────

function DisableTwoFactorForm({ onDisabled }: { onDisabled: () => void }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const disableMutation = useMutation({
    mutationFn: () => disableTwoFactor(password, code),
    onSuccess: () => {
      toast.success("Two-factor authentication disabled.");
      setOpen(false);
      setPassword("");
      setCode("");
      onDisabled();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to disable 2FA.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!password || !code) {
      setError("Enter your password and a verification code.");
      return;
    }
    disableMutation.mutate();
  };

  if (!open) {
    return (
      <Button variant="destructive" onClick={() => setOpen(true)}>
        Disable 2FA
      </Button>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-border p-4">
      {error && <div className={ERROR_BOX}>{error}</div>}
      <div className="space-y-1.5">
        <Label htmlFor="disable-password">Password</Label>
        <Input
          id="disable-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="disable-code">2FA code</Label>
        <Input
          id="disable-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          minLength={6}
          maxLength={9}
          autoComplete="one-time-code"
        />
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setOpen(false);
            setPassword("");
            setCode("");
            setError(null);
          }}
          disabled={disableMutation.isPending}
        >
          Cancel
        </Button>
        <Button type="submit" variant="destructive" disabled={disableMutation.isPending}>
          {disableMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable 2FA"}
        </Button>
      </div>
    </form>
  );
}

// ─── 2FA section ────────────────────────────────────────────────────────────

function TwoFactorSection() {
  const queryClient = useQueryClient();
  const { data: status, isPending, isError } = useQuery({
    queryKey: ["two-factor-status"],
    queryFn: getTwoFactorStatus,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["two-factor-status"] });

  if (isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (isError || !status) return <p className="text-sm text-muted-foreground">Failed to load 2FA status.</p>;

  if (status.enabled) {
    return (
      <div className="space-y-4">
        <DetailRow
          label="Status"
          value={
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F3F4F6] px-2.5 py-1 text-xs font-medium text-[#111827]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#10B981]" />
              {status.enabledAt ? `Enabled on ${formatTimestamp(status.enabledAt)}` : "Enabled"}
            </span>
          }
        />
        <DisableTwoFactorForm onDisabled={refresh} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DetailRow
        label="Status"
        value={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F3F4F6] px-2.5 py-1 text-xs font-medium text-[#111827]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#EF4444]" />
            Disabled
          </span>
        }
      />
      <EnableTwoFactorWizard onEnabled={refresh} />
    </div>
  );
}

// ─── page ───────────────────────────────────────────────────────────────────

function ProfilePage() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">Your account and security settings.</p>

      <SectionCard title="Account">
        <IdentitySection />
      </SectionCard>

      <SectionCard
        title="Two-Factor Authentication"
        description="Require a verification code in addition to your password when signing in."
      >
        <TwoFactorSection />
      </SectionCard>
    </div>
  );
}
